import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Briefcase,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Grid3X3,
  Info,
  List,
  MapPin,
  MessageCircle,
  RotateCcw,
  Search,
  Users,
  X,
} from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import DashboardLayout from '../../../components/DashboardLayout';
import CreateGroupModal from '../../../components/groups/CreateGroupModal';
import Toast from '../../../components/Toast/Toast';
import SaveListModal from '../../../components/savedLists/SaveListModal';
import AddPeopleToGroupModal from '../../../components/groups/AddMemberToGroupModal';
import AvailabilityExceptionsModal, {
  formatExceptionDate,
  type AvailabilityExceptionItem,
} from '../../../components/common/AvailabilityExceptionsModal';
import {
  Admin_AvailabilityType_LIST,
  Admin_ITSpecialty_LIST,
  Admin_Skills_LIST,
  GetRefineMaches,
  GetAllGroups,
  AddMembersToExistingGroup,
} from '../../../constants/adminData';
import { createSavedList } from '../../../services/savedListService';
import type { GroupDto } from '../../../types/groups';
import type { UserProfile } from '../../../types/UserProfile';
import { PhotoImage } from '../../../components/common/PhotoImage';
import { useNavigate } from 'react-router-dom';
import { getSensitiveData, saveSensitiveData } from '../../../utils/secureStorage';
//import { useMenuLabels } from '../../../hooks/useMenuLabels';
//import { useFieldLabel } from '../../../useFieldLabel';
import { useFieldLabel } from '../../../hooks/useFieldLabel';

type ViewMode = 'grid' | 'list';
type SortOption = 'Most Relevant' | 'Experience High To Low' | 'Experience Low To High' | 'Name A-Z' | 'Name Z-A';

type AppliedFilters = {
  skillsIds: string | null;
  itspecialityIds: string | null;
  availabilityTypeId: string | null;
  minExperience: string | null;
};

interface FindPeopleProps {
  onBack?: () => void;
  onContinue?: () => void;
}

const SORT_OPTIONS: SortOption[] = [
  'Most Relevant',
  'Experience High To Low',
  'Experience Low To High',
  'Name A-Z',
  'Name Z-A',
];

const EMPTY_APPLIED_FILTERS: AppliedFilters = {
  skillsIds: null,
  itspecialityIds: null,
  availabilityTypeId: null,
  minExperience: null,
};

function getPersonName(person: any) {
  return `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.userName || person.username || 'Unknown User';
}

function getInitials(person: any) {
  const first = person.firstName?.charAt(0) || '';
  const last = person.lastName?.charAt(0) || '';
  return `${first}${last}`.toUpperCase() || 'U';
}

function getAvailability(person: any) {
  return person?.availabilities?.[0];
}

function getAvailabilityExceptions(person: any): AvailabilityExceptionItem[] {
  return Array.isArray(person?.availabilitySummary?.upcomingExceptions)
    ? person.availabilitySummary.upcomingExceptions
    : [];
}

type SearchableOption = { value: string; label: string };

function SearchableDropdown({
  options,
  value,
  selectedValues = [],
  placeholder,
  multiple = false,
  searchable = true,
  onSelect,
  onClear,
  style,
}: {
  options: SearchableOption[];
  value?: string;
  selectedValues?: string[];
  placeholder: string;
  multiple?: boolean;
  searchable?: boolean;
  onSelect: (value: string) => void;
  onClear?: () => void;
  style?: React.CSSProperties;
}) {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const closeDropdown = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', closeDropdown);
    return () => document.removeEventListener('mousedown', closeDropdown);
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
 const visibleOptions = [...options]
  .filter((option) =>
    option.label.toLowerCase().includes(normalizedQuery)
  )
  .sort((first, second) => {
    // Keep empty/default option FIRST
    if (first.value === '') return -1;
    if (second.value === '') return 1;

    // Keep "Other" LAST
    const firstIsOther =
      first.label.trim().toLowerCase() === 'other';

    const secondIsOther =
      second.label.trim().toLowerCase() === 'other';

    if (firstIsOther !== secondIsOther) {
      return firstIsOther ? 1 : -1;
    }

    return first.label.localeCompare(second.label);
  });
  const selectedOption = options.find((option) => option.value === value);
  const buttonLabel = multiple
    ? selectedValues.length > 0
      ? `${selectedValues.length} selected`
      : placeholder
    : selectedOption?.label || placeholder;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="
    flex
    h-[43px]
    w-full
    items-center
    justify-between
    rounded-lg
    px-3
    text-left
    text-sm
    outline-none
    transition
    hover:border-blue-400
    focus:border-blue-500
  "
        style={{
          ...style,
          boxSizing: 'border-box',
        }}
      >
        <span className="truncate">
          {buttonLabel}
        </span>

        <div className="ml-2 flex shrink-0 items-center gap-1">
          {!multiple && value && onClear && (
            <span
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                onClear();
                setQuery('');
              }}
              className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Clear selection"
            >
              <X className="h-4 w-4" />
            </span>
          )}

          <ChevronDown
            className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''
              }`}
          />
        </div>
      </button>

      {isOpen && (
        <div
          className="absolute z-40 mt-1 w-full overflow-hidden rounded-lg border shadow-lg"
          style={{ backgroundColor: theme.surface, borderColor: theme.border }}
        >
          {searchable && (
            <div className="border-b border-gray-100 p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                <input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Type to search..."
                  className="w-full rounded-md border py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 placeholder:text-slate-400"
                  style={{
                    backgroundColor: theme.surfaceHover,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                />
              </div>
            </div>
          )}
          <div className="max-h-56 overflow-y-auto py-1">
            {visibleOptions.length > 0 ? (
              visibleOptions.map((option) => {
                const isSelected = multiple ? selectedValues.includes(option.value) : value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onSelect(option.value);
                      setQuery('');
                      if (!multiple) setIsOpen(false);
                    }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50"
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && <Check className="h-4 w-4 shrink-0 text-blue-600" />}
                  </button>
                );
              })
            ) : (
              <p className="px-3 py-4 text-center text-sm text-gray-500">No matching options</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FindPeople(_props: FindPeopleProps) {
  const { theme } = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortBy, setSortBy] = useState<SortOption>('Most Relevant');
  const [peopleData, setPeopleData] = useState<any[]>([]);
  const [skills, setSkills] = useState<any[]>([]);
  const [itSpecialties, setItSpecialties] = useState<any[]>([]);
  const [availabilityStatuses, setAvailabilityStatuses] = useState<any[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<any[]>([]);
  const [selectedITSpecialty, setSelectedITSpecialty] = useState('');
  const [selectedAvailability, setSelectedAvailability] = useState('');
  const [selectedExperience, setSelectedExperience] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(EMPTY_APPLIED_FILTERS);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const [selectedPeopleById, setSelectedPeopleById] = useState<Record<string, UserProfile>>({});
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [isSaveListOpen, setIsSaveListOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const [exceptionsModalPerson, setExceptionsModalPerson] = useState<any>(null);

  const cardStyle = { backgroundColor: theme.surface, borderColor: theme.border, color: theme.text };
  const mutedTextStyle = { color: theme.textSecondary };
  const inputStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    color: theme.text,
  };
  const [isAddToGroupOpen, setIsAddToGroupOpen] = useState(false);
  const [groups, setGroups] = useState<any[]>([]);
  const navigate = useNavigate();
  const isNavigatingToProfileRef = useRef(false);
  //const labels = useMenuLabels();
  const listLabel = useFieldLabel('MENU_LIST', 'List');

  const exploreLabel = useFieldLabel('MENU_EXPLORE_PEOPLE', 'Explore People');
  const label = useFieldLabel('MENU_PRIVATE_LIST', 'Private List');
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [skillsRes, specialtyRes, availabilityRes] = await Promise.all([
          Admin_Skills_LIST(),
          Admin_ITSpecialty_LIST(),
          Admin_AvailabilityType_LIST(),
        ]);

        setSkills(Array.isArray(skillsRes?.data) ? skillsRes.data : Array.isArray(skillsRes) ? skillsRes : []);
        setItSpecialties(Array.isArray(specialtyRes?.data) ? specialtyRes.data : Array.isArray(specialtyRes) ? specialtyRes : []);
        setAvailabilityStatuses(
          Array.isArray(availabilityRes?.data) ? availabilityRes.data : Array.isArray(availabilityRes) ? availabilityRes : []
        );
      } catch (error) {
        console.error('Dropdown API Error:', error);
      }
    };

    loadDropdowns();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
      setCurrentPage(1);
    }, 500);

    return () => window.clearTimeout(timer);
  }, [searchText]);

  const buildAppliedFilters = useCallback((): AppliedFilters => {
    return {
      skillsIds: selectedSkills.length > 0 ? selectedSkills.map((x) => x.skillId).join(',') : null,
      itspecialityIds: selectedITSpecialty ? selectedITSpecialty.toString() : null,
      availabilityTypeId: selectedAvailability || null,
      minExperience: selectedExperience || null,
    };
  }, [selectedAvailability, selectedExperience, selectedITSpecialty, selectedSkills]);

  const handleViewProfile = useCallback(async (person: any) => {
    const selectedUserId = person?.userId ? String(person.userId) : '';

    if (!selectedUserId || isNavigatingToProfileRef.current) {
      return;
    }

    try {
      isNavigatingToProfileRef.current = true;
      await saveSensitiveData("selectedMatchId", selectedUserId);
      await saveSensitiveData("selectedMatchProfile", JSON.stringify(person));

      navigate("/MemberProfile");
    } catch (error) {
      console.error("Error opening profile:", error);
      isNavigatingToProfileRef.current = false;
    }
  }, [navigate]);

  const fetchPeople = useCallback(async (pageNumber: number, usernameContains: string) => {
    try {
      setLoading(true);
      const payload = {
        usernameContains: usernameContains || null,
        skillsIds: appliedFilters.skillsIds,
        itspecialityIds: appliedFilters.itspecialityIds,
        availabilityTypeId: appliedFilters.availabilityTypeId,
        minExperience: appliedFilters.minExperience,
        pageNumber,
        pageSize: 60,
      };

      const response = await GetRefineMaches(payload);
      setPeopleData(response?.data?.matches || []);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error('Filter API Error:', error);
      setToast({ message: 'Unable to load people. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchPeople(currentPage, debouncedSearchText);
  }, [currentPage, debouncedSearchText, fetchPeople]);

  const sortedPeople = useMemo(() => {
    const people = [...peopleData];

    switch (sortBy) {
      case 'Experience High To Low':
        return people.sort((a, b) => (Number(b.totalExperienceInYears) || 0) - (Number(a.totalExperienceInYears) || 0));
      case 'Experience Low To High':
        return people.sort((a, b) => (Number(a.totalExperienceInYears) || 0) - (Number(b.totalExperienceInYears) || 0));
      case 'Name A-Z':
        return people.sort((a, b) => getPersonName(a).localeCompare(getPersonName(b)));
      case 'Name Z-A':
        return people.sort((a, b) => getPersonName(b).localeCompare(getPersonName(a)));
      default:
        return people;
    }
  }, [peopleData, sortBy]);

  // Keep the selected member details independent from the visible results. A
  // filter can remove a selected person from peopleData, but they must still
  // be included when creating a Private List or group.
  const selectedUsers = useMemo(
    () => selectedPeople
      .map((userId) => selectedPeopleById[userId])
      .filter((person): person is UserProfile => Boolean(person)),
    [selectedPeople, selectedPeopleById]
  );
  const hasSelectedPeople = selectedPeople.length > 0;

  const clearSelectedPeople = () => {
    setSelectedPeople([]);
    setSelectedPeopleById({});
  };

  const handleSkillSelect = (skill: any) => {
    setSelectedSkills((current) =>
      current.some((item) => item.skillId === skill.skillId)
        ? current.filter((item) => item.skillId !== skill.skillId)
        : [...current, skill]
    );
  };
  const handleRemoveSkill = (skill: any) => {
    setSelectedSkills((current) => {
      const updatedSkills = current.filter(
        (item) => item.skillId !== skill.skillId
      );

      setAppliedFilters((currentFilters) => ({
        ...currentFilters,
        skillsIds:
          updatedSkills.length > 0
            ? updatedSkills.map((item) => item.skillId).join(',')
            : null,
      }));

      setCurrentPage(1);

      return updatedSkills;
    });
  };

  const handleApplyFilters = () => {
    setAppliedFilters(buildAppliedFilters());
    setCurrentPage(1);
    setDebouncedSearchText(searchText.trim());
  };

  const handleClearFilters = () => {
    setSelectedSkills([]);
    setSelectedITSpecialty('');
    setSelectedAvailability('');
    setSelectedExperience('');
    setAppliedFilters(EMPTY_APPLIED_FILTERS);
    setSearchText('');
    setDebouncedSearchText('');
    setCurrentPage(1);
    setTotalPages(1);
  };

  const togglePersonSelection = (person: any) => {
    const userId = String(person.userId);
    setSelectedPeople((current) => (current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId]));
    setSelectedPeopleById((current) => {
      if (current[userId]) {
        const { [userId]: _removedPerson, ...remainingPeople } = current;
        return remainingPeople;
      }

      return { ...current, [userId]: person as UserProfile };
    });
  };

  const handleCreateGroupClick = () => {
    if (!hasSelectedPeople) {
      return;
    }

    setIsCreateGroupOpen(true);
  };

  const handleGroupCreated = (group: GroupDto) => {
    setToast({ message: `${listLabel} created successfully.`, type: 'success' });
    clearSelectedPeople();
  };

  const renderPhoto = (person: any, widthClass: string) => {
    const containerClass = `${widthClass} self-stretch min-h-full flex-shrink-0 overflow-hidden rounded-xl bg-gray-200`;
    const fallback = (
      <div className="flex h-full min-h-[88px] w-full items-center justify-center text-xl font-bold text-gray-500">
        {getInitials(person)}
      </div>
    );

    return (
      <div className={containerClass}>
        {person.photoId && person.photoId > 0 ? (
          <PhotoImage
            photoId={String(person.photoId)}
            alt={getPersonName(person)}
            className="h-full min-h-[88px] w-full object-cover"
            fallback={fallback}
          />
        ) : (
          fallback
        )}
      </div>
    );
  };

  const renderPersonContent = (person: any) => {
    const availability = getAvailability(person);
    const exceptions = getAvailabilityExceptions(person);
    const [nextException, ...upcomingExceptions] = exceptions;
    const nextExceptionDate = nextException ? formatExceptionDate(nextException.startDate) : null;

    return (
      <>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold leading-5" style={{ color: theme.text }}>
              {getPersonName(person)}
            </h3>
            {person.itSpecialtyName ? (
              <p className="mt-0.5 text-xs" style={mutedTextStyle}>
                {person.itSpecialtyName}
              </p>
            ) : null}
            {person.currentLocation ? (
              <p className="mt-1 flex items-center gap-1 text-xs" style={mutedTextStyle}>
                <MapPin className="h-3 w-3" />
                {person.currentLocation}
              </p>
            ) : null}
          </div>
          <span className={availability?.isAvailable ? "rounded-md bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700" : "rounded-md bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700"}>
            {availability?.isAvailable ? 'Available' : 'Not Available'}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
            <Briefcase className="h-3 w-3" />
            {person.totalExperienceInYears || 0} Years
          </span>
          {availability ? (
            <span className="rounded-md bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
              {availability.availableHoursPerWeek && availability?.isAvailable ? availability.availableHoursPerWeek : 0} hrs/week
              {availability.availabilityTypeName ? ` - ${availability.availabilityTypeName}` : ''}
            </span>
          ) : null}
        </div>

        {nextException ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span className="flex items-center gap-1 font-medium text-amber-700">
              <Calendar className="h-3 w-3" />
              Next Change: {nextExceptionDate || 'TBD'} → {nextException.availableHours ?? 0} hrs/week
            </span>
            {upcomingExceptions.length > 0 ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setExceptionsModalPerson(person);
                }}
                className="flex items-center gap-1 font-semibold text-blue-600 hover:text-blue-800"
              >
                +{upcomingExceptions.length} upcoming change{upcomingExceptions.length > 1 ? 's' : ''}
                <Info className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        ) : null}

        {person.skills?.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {person.skills.slice(0, 5).map((skill: any) => (
              <span key={skill.skillId} className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {skill.skillName}
              </span>
            ))}
            </div>
            ) : null}
      </>
    );
  };
  const loadGroups = async () => {
    try {
      const response = await GetAllGroups();


      setGroups(response?.data || []);
    } catch (error) {
      console.error(error);
    }
  };
  const handleOpenAddToGroupModal = async () => {
    if (selectedPeople.length === 0) {
      setToast({
        message: "Please select at least one user.",
        type: "warning",
      });

      return;
    }

    await loadGroups();

    setIsAddToGroupOpen(true);
  };
  const handleSaveGroupMembers = async (
    payload: any
  ) => {
    try {
      const token =
        localStorage.getItem("token") || "";

      const response =
        await AddMembersToExistingGroup(
          payload,
          token
        );

      setToast({
        message:
          response?.message ||
          "Members added successfully.",
        type: response?.success
          ? "success"
          : "warning",
      });

      if (response?.success) {
        setIsAddToGroupOpen(false);

        clearSelectedPeople();
      }
    } catch (error: any) {
      console.error(error);

      setToast({
        message:
          error?.message ||
          "Failed to add members.",
        type: "error",
      });
    }
  };

  const handleSaveListClick = () => {
    if (!hasSelectedPeople) {
      setToast({ message: 'Please select at least one user.', type: 'warning' });
      return;
    }

    setIsSaveListOpen(true);
  };

  const handleSaveList = async (payload: any) => {
    try {
      const userId = (await getSensitiveData('userId')) || '';
      const createdByName = localStorage.getItem('currentUserName') || 'You';

      createSavedList({
        ...payload,
        createdByUserId: userId,
        createdByName,
      });

      setToast({ message: `${label} created successfully`, type: 'success' });
      clearSelectedPeople();
      setIsSaveListOpen(false);
    } catch (error: any) {
      console.error(error);
      setToast({ message: error?.message || 'Unable to create Private List.', type: 'error' });
    }
  };

  const handleCloseSaveList = () => {
    setIsSaveListOpen(false);
  };

  return (
    <DashboardLayout title={exploreLabel}>
      <div className="space-y-5">
        {/* Page Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {/* <h1 className="text-2xl font-bold" style={{ color: theme.text }}>
              {exploreLabel}
            </h1> */}
            <p className="mb-0 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base" style={mutedTextStyle}>
              Search professionals by skills, expertise, and availability.
            </p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={mutedTextStyle} />
            <input
              type="text"
              placeholder="Search by name, username, or skill..."
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              className="w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm outline-none transition"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-lg border p-5 shadow-sm" style={cardStyle}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Skills
              </label>

              <SearchableDropdown
                multiple
                placeholder="Select or search skills"
                selectedValues={selectedSkills.map(
                  (skill: any) => String(skill.skillId)
                )}
                options={skills.map((skill: any) => ({
                  value: String(skill.skillId),
                  label: skill.skillName,
                }))}
                onSelect={(skillId) => {
                  const selected = skills.find(
                    (skill: any) =>
                      String(skill.skillId) === skillId
                  );

                  if (selected) {
                    handleSkillSelect(selected);
                  }
                }}
                style={inputStyle}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                IT Specialty
              </label>

              <SearchableDropdown
                value={selectedITSpecialty}
                placeholder="Select IT Specialty"
                options={itSpecialties.map((item: any) => ({
                  value: String(item.itSpecialtyId),
                  label: item.itSpecialtyName,
                }))}
                onSelect={setSelectedITSpecialty}
                style={inputStyle}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Experience
              </label>
              <SearchableDropdown
                searchable={false}
                value={selectedExperience}
                placeholder="All Experience"
                options={[
                  { value: '', label: 'All Experience' },
                  { value: '1', label: '0–1 Years' },
                  { value: '2', label: '1–3 Years' },
                  { value: '3', label: '3–5 Years' },
                  { value: '4', label: '5+ Years' },
                ]}
                onSelect={setSelectedExperience}
                style={inputStyle}
              />
            </div>

            <div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Availability Status
                </label>

                <SearchableDropdown
                  searchable={false}
                  value={selectedAvailability}
                  placeholder="All Status"
                  options={[
                    { value: '', label: 'All Status' },
                    ...availabilityStatuses.map((item: any) => ({
                      value: String(item.availabilityTypeId),
                      label: item.availabilityTypeName,
                    })),
                  ]}
                  onSelect={setSelectedAvailability}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {selectedSkills.map((skill: any) => (
                <span key={skill.skillId} className="inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                  {skill.skillName}
                  <button
                    type="button"
                    onClick={() => handleRemoveSkill(skill)}
                    className="font-bold leading-none hover:text-blue-900"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <RotateCcw className="h-4 w-4" />
                Clear Filters
              </button>
              <button
                onClick={handleApplyFilters}
                className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: theme.primary }}
              >
                {loading ? 'Loading…' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>

        {/* Selection Actions */}
        <div className="flex flex-col gap-4 rounded-lg border p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between" style={cardStyle}>
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded text-xs font-bold text-white" style={{ backgroundColor: theme.primary }}>
              {selectedPeople.length}
            </span>
            <span className="text-sm font-medium" style={{ color: theme.text }}>
              People Selected
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              disabled={!hasSelectedPeople}
              onClick={handleSaveListClick}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Users className="h-4 w-4" />
              Save {label}
            </button>
            <button
              disabled={!hasSelectedPeople}
              onClick={handleOpenAddToGroupModal}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Users className="h-4 w-4" />
              Add to Existing {listLabel}
            </button>
            <button
              disabled={!hasSelectedPeople}
              onClick={handleCreateGroupClick}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Users className="h-4 w-4" />
              Create {listLabel}
            </button>
          </div>
        </div>

        {/* Sort / View Controls */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium" style={{ color: theme.text }}>
            Showing {sortedPeople.length} results
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium" style={mutedTextStyle}>Sort by:</span>
            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value as SortOption)}
              className="rounded-lg border px-3 py-2 text-sm outline-none"
              style={{ ...inputStyle, minWidth: '190px' }}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
            <div className="flex overflow-hidden rounded-lg border" style={{ borderColor: theme.border }}>
              <button
                onClick={() => setViewMode('grid')}
                className="flex h-9 w-9 items-center justify-center"
                style={{ backgroundColor: viewMode === 'grid' ? theme.primary : theme.surface, color: viewMode === 'grid' ? '#fff' : theme.text }}
                aria-label="Grid View"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className="flex h-9 w-9 items-center justify-center border-l"
                style={{
                  backgroundColor: viewMode === 'list' ? theme.primary : theme.surface,
                  color: viewMode === 'list' ? '#fff' : theme.text,
                  borderColor: theme.border,
                }}
                aria-label="List View"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Grid / List */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sortedPeople.map((person: any) => (
              <article key={person.userId} className="relative flex flex-col rounded-lg border shadow-sm" style={cardStyle}>
                <input
                  type="checkbox"
                  checked={selectedPeople.includes(String(person.userId))}
                  onChange={() => togglePersonSelection(person)}
                  className="absolute left-3 top-3 h-4 w-4 accent-blue-600"
                  aria-label={`Select ${getPersonName(person)}`}
                />
                <div className="flex flex-1 items-stretch gap-3 p-3 pl-8">
                  {renderPhoto(person, 'w-24')}
                  <div className="min-w-0 flex-1">{renderPersonContent(person)}</div>
                </div>
                <div className="flex justify-end border-t px-3 py-2.5" style={{ borderColor: theme.border }}>
                  <button
                    onClick={() => handleViewProfile(person)}
                    className="flex items-center gap-1 text-sm font-semibold"
                    style={{ color: theme.primary }}
                  >
                    View Profile
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {sortedPeople.map((person: any) => (
              <article key={person.userId} className="flex flex-col gap-3 rounded-lg border p-3 shadow-sm md:flex-row md:items-center" style={cardStyle}>
                <div className="flex items-center gap-3 md:w-72">
                  <input
                    type="checkbox"
                    checked={selectedPeople.includes(String(person.userId))}
                    onChange={() => togglePersonSelection(person)}
                    className="h-4 w-4 flex-shrink-0 accent-blue-600"
                    aria-label={`Select ${getPersonName(person)}`}
                  />
                  {renderPhoto(person, 'w-20')}
                  <div className="flex min-w-0 flex-col justify-center">
                    <h3 className="truncate text-sm font-bold" style={{ color: theme.text }}>
                      {getPersonName(person)}
                    </h3>
                    <p className="truncate text-xs" style={mutedTextStyle}>
                      {person.itSpecialtyName || 'Specialty not available'}
                    </p>
                  </div>
                </div>
                <div className="min-w-0 flex-1">{renderPersonContent(person)}</div>
                <div className="flex justify-end md:w-32">
                  <button
                    onClick={() => handleViewProfile(person)}
                    className="flex items-center gap-1 text-sm font-semibold"
                    style={{ color: theme.primary }}
                  >
                    View Profile
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 ? (
          <div className="flex items-center justify-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((current) => Math.max(current - 1, 1))}
              className="flex h-9 w-9 items-center justify-center rounded-lg border disabled:opacity-50"
              style={inputStyle}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className="h-9 w-9 rounded-lg text-sm font-medium"
                style={{
                  backgroundColor: currentPage === page ? theme.primary : theme.surface,
                  color: currentPage === page ? theme.contrastText : theme.text,
                  border: `1px solid ${theme.border}`,
                }}
              >
                {page}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((current) => Math.min(current + 1, totalPages))}
              className="flex h-9 w-9 items-center justify-center rounded-lg border disabled:opacity-50"
              style={inputStyle}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </div>

      <AddPeopleToGroupModal
        open={isAddToGroupOpen}
        selectedUsers={selectedUsers}
        groups={groups}
        onClose={() => setIsAddToGroupOpen(false)}
        onSave={handleSaveGroupMembers}
      />
      <CreateGroupModal
        open={isCreateGroupOpen}
        selectedUsers={selectedUsers}
        onClose={() => setIsCreateGroupOpen(false)}
        onCreated={handleGroupCreated}
      />
      <SaveListModal
        open={isSaveListOpen}
        selectedMembers={selectedUsers}
        onClose={handleCloseSaveList}
        onSave={handleSaveList}
      />
      <AvailabilityExceptionsModal
        open={!!exceptionsModalPerson}
        onClose={() => setExceptionsModalPerson(null)}
        title="Upcoming Availability Changes"
        subtitle={exceptionsModalPerson ? getPersonName(exceptionsModalPerson) : undefined}
        exceptions={getAvailabilityExceptions(exceptionsModalPerson)}
      />

      {toast ? <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} /> : null}
    </DashboardLayout>
  );
}
